import m, {Component, Vnode} from "mithril";
import BaseComponent from "../../BaseComponent";
import {
    MediaPlayerActions,
    bindMediaPlayerActions,
    isUnsupportedMediaError,
    readMediaPlayerActions,
    renderMediaPlayerActions,
    renderUnsupportedNotice,
    showUnsupportedFormatDialog,
} from "../../MediaPlayer/mediaPlayerActions";

export default class AudioPlayer extends BaseComponent implements Component {

    private audio: HTMLAudioElement | null = null;
    private src = '';
    private playing = false;
    private currentTime = 0;
    private duration = 0;
    private seeking = false;
    private filename = '';
    private unsupported = false;
    private playRequested = false;
    private dialogShown = false;
    private actions: MediaPlayerActions | null = null;

    public init(): void {
        const src = this.readAttribute('src');
        if (!src) {
            console.warn('AudioPlayer: missing data-src');
            return;
        }

        this.src = src;
        this.filename = this.readAttribute('filename') ?? '';
        this.actions = readMediaPlayerActions(this.$component[0]);

        const audio = new Audio();
        audio.preload = 'metadata';
        audio.src = src;
        this.audio = audio;

        audio.addEventListener('error', () => this.onMediaError());

        audio.addEventListener('loadedmetadata', () => {
            this.duration = Number.isFinite(audio.duration) ? audio.duration : 0;
            m.redraw();
        });
        audio.addEventListener('timeupdate', () => {
            if (!this.seeking) {
                this.currentTime = audio.currentTime;
                m.redraw();
            }
        });
        audio.addEventListener('play', () => {
            this.playing = true;
            m.redraw();
        });
        audio.addEventListener('pause', () => {
            this.playing = false;
            m.redraw();
        });
        audio.addEventListener('ended', () => {
            this.playing = false;
            this.currentTime = 0;
            m.redraw();
        });

        m.mount(this.$component[0], this);

        if (this.actions) {
            bindMediaPlayerActions(this.$component[0]);
        }
    }

    public beforeRemove(): void {
        if (this.audio) {
            this.audio.pause();
            this.audio.src = '';
            this.audio = null;
        }
        if (this.$component[0]) {
            m.mount(this.$component[0], null);
        }
    }

    private toggle(): void {
        if (!this.audio) {
            return;
        }
        if (this.playing) {
            this.audio.pause();
        } else {
            this.playRequested = true;
            const playPromise = this.audio.play();
            if (playPromise) {
                playPromise.catch(() => {
                    if (this.audio && isUnsupportedMediaError(this.audio)) {
                        this.showDownloadDialog();
                    }
                });
            }
        }
    }

    private onMediaError(): void {
        if (!this.audio || !isUnsupportedMediaError(this.audio)) {
            return;
        }
        if (this.playRequested) {
            this.showDownloadDialog();
        } else if (!this.unsupported) {
            this.unsupported = true;
            m.redraw();
        }
    }

    private showDownloadDialog(): void {
        if (this.dialogShown) {
            return;
        }
        this.dialogShown = true;
        void showUnsupportedFormatDialog(this.src, this.filename, this.translator);
    }

    private seekToEvent(track: HTMLElement, clientX: number): void {
        if (!this.audio || this.duration === 0) {
            return;
        }
        const rect = track.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        this.currentTime = ratio * this.duration;
        this.audio.currentTime = this.currentTime;
    }

    private onTrackPointerDown(e: PointerEvent): void {
        const track = e.currentTarget as HTMLElement;
        this.seeking = true;
        this.seekToEvent(track, e.clientX);

        const onMove = (ev: PointerEvent) => {
            this.seekToEvent(track, ev.clientX);
            m.redraw();
        };
        const onUp = () => {
            this.seeking = false;
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            m.redraw();
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }

    private static formatTime(seconds: number): string {
        const total = Math.floor(seconds);
        const min = Math.floor(total / 60);
        const sec = total % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    }

    public view(): Vnode {
        const ratio = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;

        return m('.audio-player-wrapper.d-inline-flex.flex-column.align-items-start', [
            m('.audio-player.d-inline-flex.align-items-center.gap-2', [
                m('button.audio-player-toggle.btn.p-0.border-0', {
                    type: 'button',
                    onclick: () => this.toggle(),
                    'aria-label': this.playing ? this.translator.translate('pause') : this.translator.translate('play'),
                }, m(`i.bi.bi-${this.playing ? 'pause' : 'play'}-fill`)),
                m('.audio-player-track', {
                    onpointerdown: (e: PointerEvent) => this.onTrackPointerDown(e),
                }, m('.audio-player-track-fill', { style: { width: `${ratio}%` } })),
                m('.audio-player-time', this.duration > 0
                    ? `${AudioPlayer.formatTime(this.currentTime)} / ${AudioPlayer.formatTime(this.duration)}`
                    : '-:-- / -:--'),
                this.actions ? renderMediaPlayerActions(this.actions, this.filename, this.translator) : null,
            ]),
            this.unsupported
                ? renderUnsupportedNotice(this.src, this.filename, this.translator, '.media-player-unsupported--below-audio')
                : null,
        ]);
    }
}
